import { useRef } from 'react';
import { Box } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { Domain as DomainEnum } from '@tiendanube/common/src/enums';
import InvoiceOperationEnum from '@tiendanube/common/src/enums/InvoiceOperation';
import ModalAside from 'App/components/lab/ModalAside';
import { CancelAndSaveButtons } from 'commons/components';
import { InterfaceSetToggle } from 'commons/useToggle';
import { InvoiceOperationReason } from './Reason';

interface InvoiceOperationModalProps {
  readonly number: string;
  readonly show: boolean;
  readonly hideModal: InterfaceSetToggle;
  readonly isLoading: boolean;
  readonly operation: InvoiceOperationEnum;
  readonly operationReason: string;
  readonly setOperationReason: (reason: string) => void;
  readonly onConfirm: (operation: InvoiceOperationEnum) => Promise<void>;
}

function InvoiceOperationModal({
  number,
  show,
  hideModal,
  isLoading,
  operation,
  operationReason,
  setOperationReason,
  onConfirm,
}: Readonly<InvoiceOperationModalProps>): JSX.Element {
  const { t } = useTranslation([DomainEnum.ORDERS]);

  const handleConfirm = async () => {
    await onConfirm(operation);
    hideModal();
  };
  const initialReason = useRef(operationReason);
  const minReasonLength = 15;
  const maxReasonLength = 255;
  const isSaveDisabled =
    isLoading ||
    operationReason.length < minReasonLength ||
    operationReason.trim() === '' ||
    operationReason === initialReason.current;

  return (
    <ModalAside
      isOpen={show}
      onDidDismiss={hideModal}
      headerContent={t(`invoiceModal.${operation.toLowerCase()}.title`, {
        number: number,
      })}
    >
      <Box>
        <InvoiceOperationReason
          reason={operationReason}
          onChange={setOperationReason}
          operation={operation}
          minReasonLength={minReasonLength}
          maxReasonLength={maxReasonLength}
        />
        <CancelAndSaveButtons
          saveText={t(`invoiceModal.${operation.toLowerCase()}.buttons.submit`)}
          cancelText={t(
            `invoiceModal.${operation.toLowerCase()}.buttons.cancel`,
          )}
          onCancel={hideModal}
          onSave={handleConfirm}
          isDisabled={isSaveDisabled}
          isLoading={isLoading}
        />
      </Box>
    </ModalAside>
  );
}

export default InvoiceOperationModal;
